import { formatDate } from '@dcp/shared/utils/formatters'
import type { CashPositionMultiPeriodColumn } from '@/hooks/useCashPositionSummaryMultiPeriod'

/**
 * Missing-data warnings for the Cash Position Summary grid (DCP-161).
 *
 * The report renders `bank_balance` and `intacct_balance` through a `COALESCE(…,0)`
 * in the RPC, so a facility with no data at all is indistinguishable from one that
 * genuinely holds nothing — both read `$0`. This module turns per-facility freshness
 * COUNTS into the user-facing sentences the grid shows on a warning icon, mirroring
 * the private `computeKpiWarnings` of the Banking balance tiles (`useBanking.ts`,
 * DCP-218) that the ticket's DEV NOTE asks us to stay consistent with.
 *
 * Why counts and not the RPC's existing `as_of_date`: that column is `MAX(balances.date)`
 * per facility — and `MAX` again for pack/total rows — so a single freshly-updated
 * account hides every stale sibling, and a NULL conflates "this facility has no bank
 * accounts" with "it has accounts but no balance was ever entered". Counts express
 * partial coverage and, unlike a date, aggregate correctly by summation.
 *
 * TRIPWIRE: this module deliberately duplicates the account-selection rules of the
 * engine RPC (see the doc comments on the db-tenant helpers that feed it). That is an
 * acceptable trade for two signals: the underlying reads page (1000 rows per request)
 * and chunk their `.in()` filters at 100 ids, costing roughly `2·ceil(accounts/1000) +
 * 2·ceil(accounts/100) + 4` requests. If a THIRD or FOURTH freshness signal is ever
 * added here (transactions, outstanding checks, LOC), move the counting into
 * `get_cash_position_summary` instead — at that point the single-source-of-truth
 * argument outweighs the cost of a migration.
 *
 * Pure by design: no I/O, no React, no clock. The target date arrives with the data.
 */

/** Which cell of the balances block a warning belongs to. */
export type CashPositionWarningKey = 'bankBalance' | 'intacctBalance'

/** Warnings for ONE grid column, keyed by the row that should display them. */
export type CellWarnings = Partial<Record<CashPositionWarningKey, string>>

/**
 * Warnings for the whole grid, keyed by column: a facility id, `pack:<id>` for a
 * pack subtotal, or {@link TOTAL_COLUMN_KEY} for the frozen Combined Total column.
 */
export type CashPositionWarningMap = Map<string, CellWarnings>

/**
 * Sentinel key for the frozen Combined Total column, which has no id of its own.
 * Cannot collide with a real column key: those are UUIDs or `pack:<uuid>`.
 */
export const TOTAL_COLUMN_KEY = 'combined-total'

/** Bank-balance coverage for one facility on the target date. */
export interface BankCoverage {
  /** Active, non-deleted, facility-bound bank accounts — the denominator. */
  expected: number
  /**
   * How many of those carry a balance dated ON OR AFTER the target date.
   * `gte`, not `eq` — the target is the last day a balance was EXPECTED (Mon–Fri),
   * so a balance legitimately entered over the weekend is dated after it and still
   * counts as covered. See `getCoverageByFacility` in `db-tenant.ts`.
   */
  covered: number
}

/** Intacct-balance sync health for one facility's mapped Sage accounts. */
export interface IntacctFreshness {
  /** Mapped, active Sage bank accounts — the denominator. */
  expected: number
  /** Accounts whose last balance sync reported a failure. */
  failed: number
  /** Accounts that have never received a balance at all. */
  neverSynced: number
}

/**
 * Per-facility freshness signals, as fetched by `useCashPositionFreshness`.
 * Facilities absent from a map simply have nothing to report for that signal.
 */
export interface CashPositionFreshness {
  /** The business day the bank coverage was measured against (`YYYY-MM-DD`). */
  targetDate: string
  bankByFacility: Record<string, BankCoverage>
  intacctByFacility: Record<string, IntacctFreshness>
  /**
   * True when at least one signal could not be loaded, so its map is empty for
   * lack of data rather than for lack of problems. Warnings still degrade to
   * silence — a signal we do not have must never be rendered as missing DATA —
   * but the flag lets the caller avoid CACHING that silence (see
   * `useCashPositionFreshness`), which would otherwise leave the report
   * un-annotated for a full stale-time window after one transient error.
   */
  partial?: boolean
}

/** Empty signals, so an absent facility takes the same code path as a present one. */
const NO_BANK: BankCoverage = { expected: 0, covered: 0 }
const NO_INTACCT: IntacctFreshness = { expected: 0, failed: 0, neverSynced: 0 }

/**
 * Builds the bank-balance sentence for one column's coverage.
 *
 * Silent when `expected === 0` — a facility with no bank accounts has nothing
 * missing, and warning there would light up most columns of a typical tenant. This
 * is the same guard `computeKpiWarnings` applies to the Bank Balance tile.
 *
 * The copy never says "today": the target date is the most recent BUSINESS day, which
 * on a Monday is Friday, so naming the actual date keeps the sentence true.
 *
 * @param coverage - expected/covered account counts for the column
 * @param targetDate - the business day measured against (`YYYY-MM-DD`)
 * @returns the warning text, or undefined when the column is fully covered
 */
function bankBalanceWarning(coverage: BankCoverage, targetDate: string): string | undefined {
  const { expected, covered } = coverage
  if (expected === 0 || covered >= expected) return undefined

  const when = formatDate(targetDate)
  const where = 'the Balance Import page, under "Bank balance update"'

  // "as of", not "for": coverage counts any balance dated ON OR AFTER the target
  // business day. The target is the last day a balance was EXPECTED (Mon–Fri, since
  // banks post none at the weekend), so a balance entered on the Saturday carries a
  // later date and must still read as covered — under `eq` the user could not clear
  // the warning at all until Monday. See `getCoverageByFacility` in `db-tenant.ts`
  // for the full rationale (and for the write/read UTC skew that used to be a
  // second reason, now removed: both sides share `businessDate()`).
  if (covered === 0) {
    return `No bank balance has been entered as of ${when}. Enter ${expected === 1 ? 'it' : 'them'} on ${where}.`
  }

  const missing = expected - covered
  return (
    `${missing} of ${expected} bank accounts ${missing === 1 ? 'is' : 'are'} missing a balance ` +
    `as of ${when}. Enter the missing balance${missing === 1 ? '' : 's'} on ${where}.`
  )
}

/**
 * Builds the Intacct-balance sentence for one column's sync health.
 *
 * Only "never imported" and "last import failed" are reported. A daily-staleness rule
 * was deliberately NOT implemented (DCP-161 decision D5): the Intacct balance is a
 * monthly period figure, its sync is a manual button, and the nightly Sage cron is
 * disabled (DCP-310) — so "not refreshed today" would show amber on every facility
 * every morning forever. The Banking tiles apply exactly the same rule.
 *
 * Silent when `expected === 0`, which also covers facilities with no Sage location
 * mapping: they can never receive an Intacct balance, and nothing on the Balance
 * Import page would fix it.
 *
 * @param freshness - the column's Intacct account counts
 * @returns the warning text, or undefined when every mapped account is healthy
 */
function intacctBalanceWarning(freshness: IntacctFreshness): string | undefined {
  const { expected, failed, neverSynced } = freshness
  if (expected === 0) return undefined

  const where = 'the Balance Import page, under "Intacct balance API import"'

  // Both conditions can hold at once, especially on an aggregated pack/total
  // column. Reporting only the failures there would hide the larger problem
  // ("failed for 1 account" while forty were never imported at all), so when
  // both are present the sentence names both.
  if (failed > 0 && neverSynced > 0) {
    return (
      `The last Intacct balance import failed for ${failed} account${failed === 1 ? '' : 's'}, ` +
      `and ${neverSynced} account${neverSynced === 1 ? ' has' : 's have'} never been imported. ` +
      `Retry on ${where}.`
    )
  }
  // Failure outranks "never synced": a failing sync is actionable right now, and an
  // account can be both (a first sync that errored leaves the balance null).
  if (failed > 0) {
    return `The last Intacct balance import failed for ${failed} account${failed === 1 ? '' : 's'}. Retry it on ${where}.`
  }
  if (neverSynced > 0) {
    // The verb stays SINGULAR at any count: the subject of this sentence is "The
    // Intacct balance", and "for N accounts" is a prepositional phrase, not the
    // subject. The branch above legitimately reads "N accounts have never been
    // imported" because there the accounts ARE the subject. Both were flipped once
    // in each direction during the PR #182 review — the two forms are correct as
    // written, so agree the verb with the subject of ITS OWN clause, not with the
    // nearest number.
    return (
      `The Intacct balance for ${neverSynced} account${neverSynced === 1 ? '' : 's'} has never been imported. ` +
      `Run a sync on ${where}.`
    )
  }
  return undefined
}

/** Adds two coverage records — how a pack subtotal and the Combined Total aggregate. */
function addBank(a: BankCoverage, b: BankCoverage): BankCoverage {
  return { expected: a.expected + b.expected, covered: a.covered + b.covered }
}

/** Adds two Intacct records — same aggregation rule as {@link addBank}. */
function addIntacct(a: IntacctFreshness, b: IntacctFreshness): IntacctFreshness {
  return {
    expected: a.expected + b.expected,
    failed: a.failed + b.failed,
    neverSynced: a.neverSynced + b.neverSynced,
  }
}

/**
 * Turns per-facility freshness signals into the grid's per-column warning map.
 *
 * Aggregation (DCP-161 decision D6): a pack subtotal sums its member facilities'
 * counts, and the Combined Total sums every visible FACILITY column. Summing counts —
 * rather than OR-ing booleans — means the aggregated sentence can still say "3 of 11
 * bank accounts are missing a balance", and it gives the report a drill-down
 * invariant: an amber Combined Total guarantees at least one amber facility column to
 * its right. A pack subtotal that warned only when ALL members were stale would leave
 * a demonstrably wrong number unmarked.
 *
 * The Combined Total deliberately reads FACILITY columns only. The RPC emits every
 * visible facility as its own row — including pack members — so summing pack columns
 * as well would double-count them.
 *
 * @param input.freshness - fetched signals, or null when the fetch failed/has not
 *   landed. Null yields an EMPTY map: a missing signal must never be rendered as a
 *   missing-data warning, the same way the Banking tiles degrade.
 * @param input.columns - the rendered grid columns, in RPC order. Pack membership
 *   is read off `column.packId` — the same field the RPC stamped on the rows it
 *   summed the subtotal from — see {@link CashPositionMultiPeriodColumn.packId}
 *   for why no second source (e.g. the `useFacilities` catalog) may be consulted
 *   here instead.
 * @returns warnings keyed by column key, omitting columns with nothing to report
 */
export function buildCashPositionWarnings(input: {
  freshness: CashPositionFreshness | null
  columns: CashPositionMultiPeriodColumn[]
}): CashPositionWarningMap {
  const { freshness, columns } = input
  const warnings: CashPositionWarningMap = new Map()
  if (!freshness) return warnings

  const { targetDate, bankByFacility, intacctByFacility } = freshness

  /** Records a column's warnings, skipping columns where both signals are clean. */
  const put = (key: string, bank: BankCoverage, intacct: IntacctFreshness): void => {
    const cell: CellWarnings = {}
    const bankText = bankBalanceWarning(bank, targetDate)
    const intacctText = intacctBalanceWarning(intacct)
    if (bankText) cell.bankBalance = bankText
    if (intacctText) cell.intacctBalance = intacctText
    if (bankText || intacctText) warnings.set(key, cell)
  }

  const facilityColumns = columns.filter((c) => c.kind === 'facility')

  // Per-facility columns, and the running Combined Total.
  let totalBank = NO_BANK
  let totalIntacct = NO_INTACCT
  for (const col of facilityColumns) {
    const bank = bankByFacility[col.key] ?? NO_BANK
    const intacct = intacctByFacility[col.key] ?? NO_INTACCT
    totalBank = addBank(totalBank, bank)
    totalIntacct = addIntacct(totalIntacct, intacct)
    put(col.key, bank, intacct)
  }

  // Pack subtotal columns: sum the members that are actually on screen, so a filtered
  // view never warns about a facility the reader cannot see.
  for (const col of columns) {
    if (col.kind !== 'pack') continue
    // A pack column always carries its own id (see `packId`'s doc comment). The
    // guard exists so a malformed column can only fall silent — never match every
    // STANDALONE facility (whose `packId` is also null) and warn about facilities
    // that are not in the pack at all.
    if (col.packId === null) continue
    let bank = NO_BANK
    let intacct = NO_INTACCT
    for (const member of facilityColumns) {
      if (member.packId !== col.packId) continue
      bank = addBank(bank, bankByFacility[member.key] ?? NO_BANK)
      intacct = addIntacct(intacct, intacctByFacility[member.key] ?? NO_INTACCT)
    }
    put(col.key, bank, intacct)
  }

  put(TOTAL_COLUMN_KEY, totalBank, totalIntacct)

  return warnings
}
